import { defineStore } from 'pinia'
import { store } from '@/store'
import { cloneDeep } from 'lodash-es'
import remainingRouter from '@/router/modules/remaining'
import { flatMultiLevelRoutes, generateRoute } from '@/utils/routerHelper'
import { CACHE_KEY, useCache } from '@/hooks/web/useCache'

const { wsCache } = useCache()

export interface PermissionState {
  routers: AppRouteRecordRaw[]
  addRouters: AppRouteRecordRaw[]
  menuTabRouters: AppRouteRecordRaw[]
}

export const usePermissionStore = defineStore('permission', {
  state: (): PermissionState => ({
    routers: [],
    addRouters: [],
    menuTabRouters: []
  }),
  getters: {
    getRouters(): AppRouteRecordRaw[] {
      return this.routers
    },
    getAddRouters(): AppRouteRecordRaw[] {
      return flatMultiLevelRoutes(cloneDeep(this.addRouters))
    },
    getMenuTabRouters(): AppRouteRecordRaw[] {
      return this.menuTabRouters
    }
  },
  actions: {
    async generateRoutes(): Promise<unknown> {
      return new Promise<void>(async (resolve) => {
        // 获得菜单列表，它在登录的时候，setUserInfoAction 方法中已经进行获取
        let res: AppCustomRouteRecordRaw[] = []
        const roleRouters = wsCache.get(CACHE_KEY.ROLE_ROUTERS)
        if (roleRouters) {
          res = roleRouters as AppCustomRouteRecordRaw[]
        }
        const routerMap: AppRouteRecordRaw[] = generateRoute(res)
        // 动态路由，404一定要放到最后面
        // preschooler：vue-router@4以后已支持静态404路由，此处可不再追加
        this.addRouters = routerMap.concat([
          {
            path: '/:path(.*)*',
            // redirect: '/404',
            component: () => import('@/views/Error/404.vue'),
            name: '404Page',
            meta: {
              hidden: true,
              breadcrumb: false
            }
          }
        ])
       // 合并本地路由和远程路由，远程路由优先覆盖
        this.routers = mergeAndOverrideRoutes(cloneDeep(remainingRouter), routerMap)
        resolve()
      })
    },
    setMenuTabRouters(routers: AppRouteRecordRaw[]): void {
      this.menuTabRouters = routers
    }
  },
  persist: false
})

// 合并线上路由和本地路由，主要顺序按照后台返回的路由进行覆盖
function mergeAndOverrideRoutes(localRoutes: AppRouteRecordRaw[], remoteRoutes: AppRouteRecordRaw[]): AppRouteRecordRaw[] {
  // 定义必须保持在最前面的系统路由（按顺序）
  const frontRoutePaths = ['/redirect', '/', '/login', '/sso', '/social-login']
  
  // 定义必须保持在最后面的系统路由（按顺序）
  const endRoutePaths = ['/403', '/404', '/500', '/:pathMatch(.*)*']
  
  const localRouteMap = new Map<string, AppRouteRecordRaw>()
  localRoutes.forEach(route => {
    if (route.path) {
      localRouteMap.set(route.path, route)
    }
  })
  
  const result: AppRouteRecordRaw[] = []
  const processedPaths = new Set<string>()
  
  // 1. 首先添加前置系统路由（保持原有顺序）
  frontRoutePaths.forEach(path => {
    const route = localRouteMap.get(path)
    if (route) {
      result.push(route)
      processedPaths.add(path)
    }
  })
  
  // 2. 按照远程路由的顺序添加业务路由（排除系统路由）
  for (const remoteRoute of remoteRoutes) {
    if (remoteRoute.path && !frontRoutePaths.includes(remoteRoute.path) && !endRoutePaths.includes(remoteRoute.path)) {
      result.push(remoteRoute)
      processedPaths.add(remoteRoute.path)
    }
  }
  
  // 3. 添加其他未被覆盖的本地业务路由（如 /info 等）
  localRoutes.forEach(localRoute => {
    if (localRoute.path && !processedPaths.has(localRoute.path) && 
        !frontRoutePaths.includes(localRoute.path) && !endRoutePaths.includes(localRoute.path)) {
      result.push(localRoute)
      processedPaths.add(localRoute.path)
    }
  })
  
  // 4. 最后添加后置系统路由（404等，保持原有顺序）
  endRoutePaths.forEach(path => {
    const route = localRouteMap.get(path)
    if (route) {
      result.push(route)
      processedPaths.add(path)
    }
  })
  
  return result
}

export const usePermissionStoreWithOut = () => {
  return usePermissionStore(store)
}
